/**
 * Details of a selected edge (fact or symptom -> cause) in the graph lights side panel, and the list of a node's edges
 * (EdgeList) that NodeDetails shows so every relation is reachable from the keyboard.
 *
 * The relation itself comes from the bundle edge (kind, seen, wins); why it matters comes from tse:diag_state: the observed
 * answer, the cause's % and status, whether this fact is a counted clue of the cause (`causes[].clues`), whether it ruled the
 * cause out (`ruled_out[].by_fact`) and how much the last answer moved the cause (`causes[].delta`).
 */
import React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";

import { EDGE_KINDS, TONES } from "./lightsConstants";
import { LIGHTS_TEXT, formatPercent } from "./lightsText";

const T = LIGHTS_TEXT.edge;
const ICON_PX = 14;
const list = (v) => (Array.isArray(v) ? v : []);

function NodeLink({ nid, lights, onSelect }) {
  const node = lights.nodes.get(nid);
  if (!node) return <span>{nid}</span>;
  return <button type="button" className="d5-lights-link" onClick={() => onSelect({ node: nid })}>{node.label}</button>;
}

/** The edges of one node as buttons ("<fact> supports <cause>"), traversed ones first. */
export function EdgeList({ nid, lights, onSelect }) {
  const edges = lights.edges.filter((e) => e.src === nid || e.dst === nid).sort((a, b) => Number(b.traversed) - Number(a.traversed));
  if (!edges.length) return null;
  return (
    <details className="d5-lights-fold">
      <summary>{T.relations}<span className="d5-lights-count">{edges.length}</span></summary>
      <ul className="d5-lights-list d5-lights-edges">
        {edges.map((e) => {
          const other = lights.nodes.get(e.src === nid ? e.dst : e.src);
          return (
            <li key={e.id}>
              <button type="button" className={`d5-lights-row d5-lights-edge-row${e.traversed ? " d5-lights-traversed" : ""}`} onClick={() => onSelect({ edge: e.id })} title={T.openEdge}>
                <span className={`d5-lights-edge-swatch d5-lights-edge-swatch--${e.kind === EDGE_KINDS.RULES_OUT ? "rules_out" : e.traversed ? "lit" : "faint"}`} aria-hidden="true" />
                <span className="d5-lights-row-label">{e.src === nid ? `${T.kind[e.kind]} ${other?.label || e.dst}` : `${other?.label || e.src} ${e.kind === EDGE_KINDS.RULES_OUT ? T.rulesOut : T.supports} it`}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </details>
  );
}

function WhyItMatters({ edge, lights, diagState }) {
  const fact = lights.nodes.get(edge.src);
  const cause = list(diagState?.causes).find((c) => c.key === edge.dst) || null;
  const ruled = list(diagState?.ruled_out).find((r) => r.cause_key === edge.dst && r.by_fact === edge.src) || null;
  const clue = list(cause?.clues).find((c) => c.fact_id === edge.src) || null;
  const lines = [];
  if (fact?.observed) {
    lines.push(`${LIGHTS_TEXT.observedValue[fact.observed.value] || LIGHTS_TEXT.observedValue.unknown}${fact.observed.source ? ` · ${LIGHTS_TEXT.observedSource(fact.observed.source)}` : ""}.`);
  } else lines.push(T.noObservation);
  if (ruled) lines.push(T.ruledHere(ruled.reason));
  else if (clue) lines.push(T.clue);
  if (cause && Number.isFinite(Number(cause.delta)) && Number(cause.delta) !== 0 && (clue || ruled)) lines.push(T.moved(Number(cause.delta)));
  lines.push(T.seen(edge.seen, edge.wins));
  return (
    <section className="d5-lights-sec">
      <h4 className="d5-lights-label">{T.whyMatters}</h4>
      {lines.map((l, i) => <p key={i}>{l}</p>)}
    </section>
  );
}

export default function EdgeDetails({ edge, lights, diagState, onSelect, onBack }) {
  const fact = lights.nodes.get(edge.src);
  const cause = lights.nodes.get(edge.dst);
  const verb = edge.kind === EDGE_KINDS.RULES_OUT ? T.rulesOut : T.supports;
  const status = cause?.status && LIGHTS_TEXT.causeStatus[cause.status];
  return (
    <div className="d5-lights-panel-body">
      <button type="button" className="d5-lights-btn d5-lights-back" onClick={onBack}>
        <ArrowLeft size={ICON_PX} aria-hidden="true" />
        <span>{LIGHTS_TEXT.back}</span>
      </button>
      <div className="d5-lights-detail">
        <div className="d5-lights-head">
          <h3 className="d5-lights-detail-title">{fact?.label || edge.src} <span className="d5-lights-muted">{verb}</span> {cause?.label || edge.dst}</h3>
          <p className="d5-lights-conf">
            <span className={`d5-lights-chip d5-lights-chip--${edge.kind}`}>{T.kind[edge.kind]}</span>
            <span className={`d5-lights-chip${edge.traversed ? " d5-lights-chip--traversed" : ""}`}>{edge.traversed ? T.traversed : T.notTraversed}</span>
          </p>
        </div>
        <section className="d5-lights-sec">
          <h4 className="d5-lights-label">{T.relation}</h4>
          <dl className="d5-lights-ends">
            <div><dt>{T.from}</dt><dd><NodeLink nid={edge.src} lights={lights} onSelect={onSelect} /><span className="d5-lights-muted"> · {LIGHTS_TEXT.roles[fact?.role] || ""}</span></dd></div>
            <div aria-hidden="true" className="d5-lights-arrow"><ArrowRight size={ICON_PX} /></div>
            <div>
              <dt>{T.to}</dt>
              <dd>
                <NodeLink nid={edge.dst} lights={lights} onSelect={onSelect} />
                {cause?.pct != null && cause.tone !== TONES.EMBER && <span className="d5-lights-pct"> {formatPercent(cause.pct)}</span>}
                {status && <span className={`d5-lights-chip d5-lights-chip--${cause.status}`}>{status}</span>}
              </dd>
            </div>
          </dl>
        </section>
        <WhyItMatters edge={edge} lights={lights} diagState={diagState} />
      </div>
    </div>
  );
}
